import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, List, ListItem, ListItemText, IconButton, Stack } from '@mui/material';
import { Delete as DeleteIcon, PlayArrow as RunIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const SavedSearches: React.FC = () => {
  const { authenticated, keycloak } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('My search');
  const [query, setQuery] = useState('{"q":"immigration"}');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      setLoading(true);
      setError(null);
      try {
        const resp = await apiService.listSavedSearches();
        if (cancelled) return;
        setItems(Array.isArray(resp.data?.items) ? resp.data.items : []);
      } catch (e: any) {
        setError('Failed to load saved searches');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated, keycloak?.token]);

  const handleCreate = async () => {
    try {
      setError(null);
      const payload = { name: name.trim() || 'My search', query: JSON.parse(query) } as any;
      const resp = await apiService.createSavedSearch(payload);
      const created = resp?.data?.saved_search || resp?.data;
      if (!created?.id) throw new Error('Missing created record');
      setItems(prev => [{ ...payload, ...created }, ...prev]);
      setError(null);
      setName('');
    } catch (e: any) {
      setError('Failed to create saved search. Ensure query is valid JSON.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteSavedSearch(id);
      setItems(items.filter((x) => x.id !== id));
    } catch {}
  };

  const runFeed = async (id: number) => {
    try {
      const resp = await apiService.getFeedFromSavedSearch(id, 20, 0);
      const items = resp?.data?.items || [];
      alert(`Feed results: ${items.length} items`);
    } catch {
      alert('Failed to run feed');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Saved Searches</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Create a saved search</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" sx={{ minWidth: 220 }} />
          <TextField label="Query JSON" value={query} onChange={(e) => setQuery(e.target.value)} size="small" fullWidth />
          <Button variant="contained" onClick={handleCreate}>Save</Button>
        </Stack>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>Example query: {`{"updated_since_days":7,"text":"AI immigration"}`}</Typography>
      </Paper>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>My saved searches</Typography>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <List>
            {items.map((s: any) => (
              <ListItem
                key={s.id}
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="run" onClick={() => runFeed(s.id)} sx={{ mr: 1 }}>
                      <RunIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(s.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={s.name}
                  secondary={(() => { try { return JSON.stringify(s.query || {}, null, 0); } catch { return ''; } })()}
                />
              </ListItem>
            ))}
            {items.length === 0 && <Typography variant="body2">No saved searches yet.</Typography>}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default SavedSearches;
